import React, { useCallback, useState } from 'react';
import { List, Button, Typography } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { workOrderApi } from '../../../services/api';
import { WorkOrder } from '../../../types';
import { usePanelData } from './usePanelData';
import PanelCard from './PanelCard';
import SetEtaModal from './SetEtaModal';

const { Text } = Typography;

const vehicleLabel = (wo: WorkOrder) =>
  wo.vehicle ? `${wo.vehicle.year} ${wo.vehicle.make} ${wo.vehicle.model}` : '';
const customerLabel = (wo: WorkOrder) =>
  wo.customer ? `${wo.customer.first_name} ${wo.customer.last_name}` : '';
const daysWaiting = (iso?: string | null) => (iso ? dayjs().diff(dayjs(iso), 'day') : null);
const etaIsPast = (iso?: string | null) => !!iso && dayjs(iso).isBefore(dayjs(), 'day');

const PartsStatusPanel: React.FC = () => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<WorkOrder | null>(null);
  const fetcher = useCallback(
    () => workOrderApi.getAll({ status: 'waiting_parts', limit: 5 }).then((r) => r.data),
    []
  );
  const { data, loading, error, reload } = usePanelData<WorkOrder[]>(fetcher);
  const items = data ?? [];

  return (
    <>
      <PanelCard
        title="Parts Status"
        icon={<InboxOutlined style={{ color: '#fa8c16' }} />}
        count={items.length}
        loading={loading}
        error={error}
        isEmpty={items.length === 0}
        emptyText="No jobs waiting on parts"
        onRetry={reload}
        extra={
          items.length > 0 ? (
            <Button type="link" onClick={() => navigate('/owner/work-orders?status=waiting_parts')}>
              View all
            </Button>
          ) : null
        }
      >
        <List
          dataSource={items}
          renderItem={(wo) => (
            <List.Item
              actions={[
                <Button key="eta" size="small" onClick={() => setEditing(wo)}>
                  Set ETA
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <span
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/owner/work-orders/${wo.id}`)}
                  >
                    <Text strong>{wo.wo_number}</Text> <Text type="secondary">{customerLabel(wo)}</Text>
                  </span>
                }
                description={
                  <span>
                    {vehicleLabel(wo)}
                    {daysWaiting(wo.waiting_parts_since) !== null && (
                      <Text type="secondary"> · {daysWaiting(wo.waiting_parts_since)}d waiting</Text>
                    )}
                    {' · '}
                    {wo.parts_eta ? (
                      <Text type={etaIsPast(wo.parts_eta) ? 'danger' : undefined}>
                        ETA {dayjs(wo.parts_eta).format('MMM D')}
                        {etaIsPast(wo.parts_eta) ? ' (overdue)' : ''}
                      </Text>
                    ) : (
                      <Text type="secondary">ETA not set</Text>
                    )}
                    {wo.parts_supplier ? ` · ${wo.parts_supplier}` : ''}
                  </span>
                }
              />
            </List.Item>
          )}
        />
      </PanelCard>

      {editing && (
        <SetEtaModal
          workOrder={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </>
  );
};

export default PartsStatusPanel;
